/**
 * Runtime security settings — the shape, and the cache the bootstrap reads.
 *
 * Three hardening measures that Ops can switch without a deploy: whether a
 * Content-Security-Policy is applied, where Cognito tokens are kept, and which
 * foreign origin the Superchat preview is framed from. The document behind them
 * lives in the globalsettings table and is served by the securitySettings
 * function; see `amplify/backend/function/securitySettings/src/securitySettings.js`
 * for the authoritative validation.
 *
 * Why there is a cache at all: two of the three have to be decided before the
 * app can make its first network call. Token storage has to be chosen before
 * Amplify's Auth module is configured, and a policy is worth most when it is in
 * place before anything is fetched. Neither can wait for a round trip. So the
 * bootstrap reads the last known settings synchronously, and the fetch that
 * follows only updates the cache for next time.
 *
 * The cost of that is honest and worth stating: a change Ops makes reaches a
 * given browser on its *second* load, not its first. Rolling a broken policy
 * back is one reload, not a deploy, which is the property that mattered.
 *
 * The cache is localStorage, which any script on our origin can write. That is
 * not a hole: a script that can write it can already do everything the settings
 * defend against. It does mean the cached value is untrusted input, so it is
 * validated on the way out exactly as if it had arrived over the network.
 */

export type CspMode = 'off' | 'on';
export type TokenStorageMode = 'local' | 'session';

/**
 * The directives an operator may widen from the admin screen.
 *
 * Not every directive, and the omissions are the point. `default-src`,
 * `object-src`, `base-uri` and `form-action` exist to be closed; a text box that
 * can add to them is not a control. `img-src`, `media-src` and `frame-src` are
 * absent for the opposite reason — they already admit every https address, so
 * they cannot refuse anything and there is nothing to fix. If one of those is
 * ever narrowed, it belongs in this list at the same time.
 *
 * What is left is every directive that can actually refuse a request today.
 */
export const CSP_WIDENABLE_DIRECTIVES = ['script-src', 'connect-src', 'style-src', 'font-src', 'worker-src'] as const;

export type CspWidenableDirective = (typeof CSP_WIDENABLE_DIRECTIVES)[number];

/**
 * One address an operator has added, and the single directive it was added to.
 *
 * A bare list of hosts used to be added to `script-src` and `connect-src`
 * together, which had two faults. Fixing a blocked font granted the host script
 * execution as a side effect — a far larger permission than anyone intended —
 * and a refusal from any other directive could not be fixed from the screen at
 * all, because the host never reached it. Naming the directive fixes both.
 */
export interface CspExtraSource {
	origin: string;
	directive: CspWidenableDirective;
}

export interface SecuritySettings {
	cspMode: CspMode;
	/**
	 * Extra addresses to admit, for a host that appears between deploys. Each
	 * names the one directive it widens.
	 */
	cspExtraSources: CspExtraSource[];
	tokenStorage: TokenStorageMode;
	/** Origin the Superchat preview is framed from. Empty means the unisolated fallback. */
	previewOrigin: string;
	version: number;
}

export interface SecuritySettingsRevision extends SecuritySettings {
	updatedAt: string | null;
	updatedBy: string | null;
}

export const CSP_MODES: CspMode[] = ['off', 'on'];
export const TOKEN_STORAGE_MODES: TokenStorageMode[] = ['local', 'session'];

/**
 * Defaults are today's behaviour, deliberately. A browser with no cache, an
 * environment with no document, and a failed fetch all have to leave the app
 * exactly as it was rather than half-hardened.
 */
export const DEFAULT_SECURITY_SETTINGS: SecuritySettings = {
	cspMode: 'off',
	cspExtraSources: [],
	tokenStorage: 'local',
	previewOrigin: '',
	version: 0
};

/**
 * The same narrow host grammar the function enforces. Kept here as well because
 * the cache is not the function: a value that never passed validation could have
 * been written straight into localStorage. The admin screen uses these too, so a
 * bad origin is refused before it is sent rather than after.
 *
 * Taken apart rather than matched with one expression: every regex for a dotted
 * host puts a quantifier inside a quantifier, which is the shape a ReDoS check
 * flags — correctly in general, wrongly here, since a segment cannot contain the
 * dot that separates segments. Splitting first makes it linear by construction
 * and leaves nothing to argue about.
 */
const HOST_SEGMENT = /^[a-z0-9-]{1,63}$/;
const PORT = /^\d{1,5}$/;

const isHostWithOptionalPort = (
	value: string,
	{ allowLeadingWildcard }: { allowLeadingWildcard: boolean }
): boolean => {
	const [host, port, ...rest] = value.split(':');
	if (rest.length || (port !== undefined && !PORT.test(port))) {
		return false;
	}
	const segments = host.split('.');
	if (segments.length < 2) {
		return false;
	}
	if (allowLeadingWildcard && segments[0] === '*') {
		segments.shift();
		if (segments.length < 2) {
			return false;
		}
	}
	return segments.every((segment) => HOST_SEGMENT.test(segment));
};

const withoutScheme = (value: unknown): string | null => {
	const text = typeof value === 'string' ? value.trim() : '';
	return text.startsWith('https://') ? text.slice('https://'.length) : null;
};

/** `https://host[:port]`, optionally `https://*.host` — and nothing else. */
export const isAllowedCspHost = (value: unknown): boolean => {
	const rest = withoutScheme(value);
	return rest !== null && isHostWithOptionalPort(rest, { allowLeadingWildcard: true });
};

/** An origin and nothing more: no wildcard, no path, no query, no fragment. */
export const isBareHttpsOrigin = (value: unknown): boolean => {
	const rest = withoutScheme(value);
	if (rest === null || /[/?#]/.test(rest)) {
		return false;
	}
	return isHostWithOptionalPort(rest, { allowLeadingWildcard: false });
};

/**
 * Coerce anything into settings we are willing to act on.
 *
 * Unknown modes fall back to the default rather than being passed through, so a
 * document written by a newer version of the backend — or a hand-edited cache —
 * cannot put an unrecognised value into the policy builder or the storage
 * selector.
 */
const isWidenableDirective = (value: unknown): value is CspWidenableDirective =>
	typeof value === 'string' && (CSP_WIDENABLE_DIRECTIVES as readonly string[]).includes(value);

/**
 * Reads the extra sources, accepting the shape this field used to have.
 *
 * A document written before the directive was recorded holds a bare list of
 * hosts, and those hosts were admitted to `script-src` and `connect-src`. They
 * are migrated to exactly that pair rather than to a guess: narrowing a policy
 * somebody is relying on, silently, on the strength of a schema change, is the
 * one outcome worse than the over-granting this change exists to end.
 */
const readExtraSources = (input: { cspExtraSources?: unknown; cspExtraHosts?: unknown }): CspExtraSource[] => {
	if (Array.isArray(input.cspExtraSources)) {
		return (input.cspExtraSources as unknown[]).filter((entry): entry is CspExtraSource => {
			const candidate = entry as Partial<CspExtraSource> | null;
			return Boolean(
				candidate && isAllowedCspHost(candidate.origin) && isWidenableDirective(candidate.directive)
			);
		});
	}

	if (Array.isArray(input.cspExtraHosts)) {
		return (input.cspExtraHosts as unknown[])
			.filter((host): host is string => isAllowedCspHost(host))
			.flatMap((origin) => [
				{ origin, directive: 'script-src' as const },
				{ origin, directive: 'connect-src' as const }
			]);
	}

	return [];
};

/**
 * A recorded revision, in the shape the screen needs.
 *
 * Deliberately not `sanitiseSecuritySettings`. That coerces an unrecognised
 * value to today's default, which is right for the boot path and wrong here: a
 * version that really ran on a mode this build has retired must still say so.
 * The only thing changed is the shape — a document written before the directive
 * was recorded gets its hosts mapped to the pair they actually granted.
 *
 * It exists because it is not optional. A client can meet a backend that has not
 * been deployed yet, and this one did: the field was simply absent, and the
 * drill-down crashed reading its length.
 */
export const readRevision = (raw: unknown): SecuritySettingsRevision => {
	const input = (raw || {}) as Partial<Record<string, unknown>>;

	return {
		...(input as unknown as SecuritySettingsRevision),
		cspExtraSources: readExtraSources(input),
		updatedAt: typeof input.updatedAt === 'string' ? input.updatedAt : null,
		updatedBy: typeof input.updatedBy === 'string' ? input.updatedBy : null
	};
};

export const sanitiseSecuritySettings = (raw: unknown): SecuritySettings => {
	const input = (raw || {}) as Partial<Record<keyof SecuritySettings, unknown>> & { cspExtraHosts?: unknown };

	const cspMode = CSP_MODES.includes(input.cspMode as CspMode)
		? (input.cspMode as CspMode)
		: DEFAULT_SECURITY_SETTINGS.cspMode;

	const tokenStorage = TOKEN_STORAGE_MODES.includes(input.tokenStorage as TokenStorageMode)
		? (input.tokenStorage as TokenStorageMode)
		: DEFAULT_SECURITY_SETTINGS.tokenStorage;

	const cspExtraSources = readExtraSources(input);

	const previewOrigin =
		typeof input.previewOrigin === 'string' && isBareHttpsOrigin(input.previewOrigin) ? input.previewOrigin : '';

	const version = Number(input.version);

	return {
		cspMode,
		cspExtraSources,
		tokenStorage,
		previewOrigin,
		version: Number.isFinite(version) && version > 0 ? version : 0
	};
};

const CACHE_KEY = 'pearsana.security.settings';

/** Last known settings, or the defaults. Never throws: the boot depends on it. */
export const readCachedSecuritySettings = (): SecuritySettings => {
	try {
		const raw = window.localStorage.getItem(CACHE_KEY);
		if (!raw) {
			return { ...DEFAULT_SECURITY_SETTINGS };
		}
		return sanitiseSecuritySettings(JSON.parse(raw));
	} catch {
		// A private window, blocked storage, or a half-written value. The defaults
		// are the safe answer in all three cases.
		return { ...DEFAULT_SECURITY_SETTINGS };
	}
};

/** Records the settings for the next load. Failure is not worth surfacing. */
export const cacheSecuritySettings = (settings: SecuritySettings): void => {
	try {
		window.localStorage.setItem(CACHE_KEY, JSON.stringify(settings));
	} catch {
		// Storage is full or blocked; the app runs on this load's settings and asks
		// again next time.
	}
};

/** Used when the token storage mode changes, so the old store is not left holding tokens. */
export const SECURITY_SETTINGS_CACHE_KEY = CACHE_KEY;
